import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Icon from "../components/Icon";
import LegalFooter from "../components/LegalFooter";
import { useCookieConsent } from "../context/CookieConsentContext";
import {
  lookupSignCode,
  memorialSlugExists,
  parseMemorialSlug,
} from "../services/signService";

// ---------------------------------------------------------------------------
// The public face of Glimt — one phone screen, no scrolling.
//
// Serves every arrival that isn't a live memorial:
//   /                 someone browsing, or an owner typing their code
//   /s/:code          a scanned sign — the engraved URL
//   /activate/:code   a scanned sign from the first batches
//
// Most in-store scans are curiosity rather than a purchase, so a scan explains
// what the object in their hand is before it asks for anything. The owner's
// "set up" action sits on the same screen, and the longer story is one tap
// away — revealed in place so the page never scrolls.
//
// The sign code is never displayed. It sits under a sticker on the back of the
// sign, which is what proves the sign was actually bought, so the page never
// echoes it back.
// ---------------------------------------------------------------------------

const SHOP_URL = "https://www.glimtshop.de/";

const ink = "var(--glimt-ink)";
const inkSoft = "var(--glimt-ink-soft)";
const taupe = "var(--glimt-taupe)";
const gold = "var(--glimt-gold)";
const display = "var(--glimt-display)";

const EASE = [0.22, 1, 0.36, 1];

const titleStyle = {
  fontFamily: display,
  fontSize: "clamp(26px, 7.6vw, 36px)",
  fontWeight: 400,
  lineHeight: 1.14,
  letterSpacing: "-0.025em",
  color: ink,
  margin: 0,
  maxWidth: 330,
  // Split the headline into even lines instead of leaving one word stranded.
  textWrap: "balance",
};

const bodyStyle = {
  fontSize: "clamp(14.5px, 4vw, 16px)",
  lineHeight: 1.55,
  color: inkSoft,
  margin: 0,
  maxWidth: 330,
  textWrap: "pretty",
};

/** What Glimt is, in three lines. Revealed in place rather than on a new screen. */
const POINTS = [
  "An engraved sign, placed where they're remembered",
  "Anyone who visits can scan it and leave a photo or a note",
  "You approve every memory before it appears",
];

/**
 * A pill action. `gold` is reserved for the shop: almost everyone who lands
 * here is a visitor who scanned a sign out of curiosity, not an owner, so the
 * route to the product has to be the thing the eye lands on first.
 */
function PrimaryButton({ children, onClick, href, disabled, tone = "ink" }) {
  const style = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    width: "100%",
    maxWidth: 320,
    padding: "15px 24px",
    borderRadius: 999,
    border: "none",
    background: disabled ? "#6C7A70" : tone === "gold" ? gold : ink,
    color: tone === "gold" ? ink : "var(--glimt-cream)",
    fontFamily: "inherit",
    fontSize: 15.5,
    fontWeight: 500,
    textDecoration: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: tone === "gold" ? "0 12px 26px -14px rgba(190,150,40,0.7)" : "none",
    WebkitTapHighlightColor: "transparent",
  };

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={style}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={style}>
      {children}
    </button>
  );
}

/** Low-emphasis text action. Used for everything secondary on the screen. */
function TextButton({ children, onClick, href }) {
  const style = {
    background: "none",
    border: "none",
    padding: 4,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    color: taupe,
    textDecoration: "none",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={style}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} style={style}>
      {children}
    </button>
  );
}

const Separator = () => <span style={{ color: "rgba(30,38,32,0.18)" }}>·</span>;

/** Code / link entry. The submit control sits inside the field to save height. */
function CodeForm({ value, onChange, onSubmit, error, submitting }) {
  return (
    <form onSubmit={onSubmit} style={{ width: "100%", maxWidth: 320 }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Z96J-EYK"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          aria-label="Sign code or memorial link"
          style={{
            width: "100%",
            padding: "16px 58px 16px 18px",
            background: "#FFFDF8",
            border: `1px solid ${error ? "#C0553F" : "rgba(30,38,32,0.14)"}`,
            borderRadius: 999,
            fontSize: 16,
            fontFamily: "inherit",
            letterSpacing: "0.04em",
            color: ink,
            outline: "none",
            WebkitAppearance: "none",
          }}
        />
        <button
          type="submit"
          disabled={submitting}
          aria-label="Continue"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 42,
            height: 42,
            borderRadius: 999,
            border: "none",
            background: submitting ? "#6C7A70" : ink,
            color: "var(--glimt-cream)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: submitting ? "not-allowed" : "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <ArrowRight size={17} strokeWidth={2} />
        </button>
      </div>
      {error && (
        <p style={{ color: "#C0553F", fontSize: 13, lineHeight: 1.45, margin: "10px 2px 0" }}>
          {error}
        </p>
      )}
    </form>
  );
}

export default function Home() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();

  const [sign, setSign] = useState(null);
  const [checking, setChecking] = useState(!!codeParam);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPoints, setShowPoints] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const roomForStar = useRoomForStar();

  // Resolve a scanned code. An already-activated sign goes straight to its
  // memorial — that visitor came to see someone, not to read about us.
  useEffect(() => {
    if (!codeParam) {
      setSign(null);
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);

    lookupSignCode(codeParam)
      .then((res) => {
        if (cancelled) return;
        if (res.status === "activated") {
          if (res.slug) {
            navigate(`/m/${res.slug}`, { replace: true });
            return;
          }
          // Activated but with no page to show: a broken record rather than a
          // sign anyone should be able to claim.
          setSign({ status: "orphan", code: res.code });
          setChecking(false);
          return;
        }
        setSign(res);
        setChecking(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSign({ status: "error" });
        setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [codeParam, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const raw = input.trim();
    if (!raw) {
      setError("Enter the code from your sign, or paste a memorial link.");
      return;
    }

    setSubmitting(true);
    try {
      const slug = parseMemorialSlug(raw);
      const looksLikeLink = /^https?:\/\//i.test(raw) || raw.startsWith("/");

      if (slug && (await memorialSlugExists(slug))) {
        navigate(`/m/${slug}`);
        return;
      }
      if (looksLikeLink) {
        setError("We couldn't find a memorial at that link.");
        return;
      }

      const found = await lookupSignCode(raw);
      if (found.status === "activated" && found.slug) {
        navigate(`/m/${found.slug}`);
        return;
      }
      if (found.status === "unactivated") {
        navigate(`/s/${found.code}`);
        return;
      }
      if (found.status === "void") {
        setError("This code has been deactivated. Please contact support.");
        return;
      }
      setError("We couldn't find a sign or memorial with that code. Check it and try again.");
    } catch {
      setError("Something went wrong. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div
        className="glimt-landing glimt-screen"
        style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}
      >
        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut" }}
        >
          <Icon name="lotus" size={38} color={gold} />
        </motion.div>
        <p style={{ ...bodyStyle, fontSize: 14 }}>Reading your sign…</p>
      </div>
    );
  }

  const status = sign?.status ?? "browse";

  // A mistyped or unreadable code needs the field straight away. On the plain
  // landing page it stays behind "I have a sign".
  const showCodeField =
    status === "unknown" || status === "error" || (status === "browse" && codeOpen);

  return (
    <div className="glimt-landing glimt-screen" style={{ display: "flex", flexDirection: "column" }}>
      {/* ── Brand row ──────────────────────────────────────────────────────── */}
      {/* Just the wordmark — the shop has its own button below, so a second
          link up here would only split the attention it needs. */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "16px 22px 4px",
          flexShrink: 0,
        }}
      >
        <Icon name="lotus" size={19} color={gold} />
        <span style={{ fontFamily: display, fontSize: 19, fontWeight: 500, letterSpacing: "-0.01em" }}>
          Glimt
        </span>
      </header>

      {/* ── One screen of content, vertically centred ──────────────────────── */}
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "8px 24px 4px",
          minHeight: 0,
        }}
      >
        {/* The star gives way to the explanation when it's opened, which is
            what keeps the whole page inside one screen. */}
        <AnimatePresence initial={false}>
          {!showPoints && roomForStar && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1, marginBottom: 24 }}
              exit={{ opacity: 0, scale: 0.9, marginBottom: 0, height: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              style={{ overflow: "hidden" }}
            >
              <Icon name="lotus" size={40} color={gold} />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}
        >
          <Copy status={status} />
        </motion.div>

        {/* ── The longer story, revealed in place ──────────────────────────── */}
        <AnimatePresence initial={false}>
          {showPoints && (
            <motion.ul
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              style={{
                overflow: "hidden",
                listStyle: "none",
                margin: 0,
                padding: 0,
                width: "100%",
                maxWidth: 320,
                textAlign: "left",
              }}
            >
              <div style={{ display: "grid", gap: 11, paddingTop: 20 }}>
                {POINTS.map((p) => (
                  <li key={p} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ flexShrink: 0, marginTop: 2 }}>
                      <Icon name="lotus" size={13} color={gold} />
                    </span>
                    <span style={{ fontSize: 14.5, lineHeight: 1.45, color: inkSoft }}>{p}</span>
                  </li>
                ))}
              </div>
            </motion.ul>
          )}
        </AnimatePresence>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            marginTop: 22,
            width: "100%",
          }}
        >
          {status === "unactivated" && (
            <PrimaryButton onClick={() => navigate(`/setup/${sign.code}`)}>
              Set up this sign
              <ArrowRight size={17} strokeWidth={2} />
            </PrimaryButton>
          )}

          {showCodeField && (
            <CodeForm
              value={input}
              onChange={(v) => {
                setInput(v);
                setError("");
              }}
              onSubmit={handleSubmit}
              error={error}
              submitting={submitting}
            />
          )}

          <PrimaryButton tone="gold" href={SHOP_URL}>
            Explore the product
          </PrimaryButton>

          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
            <TextButton onClick={() => setShowPoints((v) => !v)}>
              {showPoints ? "Close" : "What is Glimt?"}
            </TextButton>
            {/* Entering a code is a once-in-the-product's-life action, so it
                waits behind a tap rather than competing with the shop. */}
            {status === "browse" && !codeOpen && (
              <>
                <Separator />
                <TextButton onClick={() => setCodeOpen(true)}>I have a sign</TextButton>
              </>
            )}
            {showCodeField && (
              <>
                <Separator />
                <TextButton onClick={() => navigate("/admin/login")}>Owner login</TextButton>
              </>
            )}
          </div>
        </div>
      </main>

      <LegalFooter
        style={{ padding: "6px 16px 12px", gap: "4px 12px", flexShrink: 0 }}
        color={taupe}
        fontFamily="inherit"
      />
    </div>
  );
}

/**
 * Whether there's vertical room for the star.
 *
 * It's the only decorative element on the page, so it's the first thing to go
 * on a short phone with the cookie banner taking the lower third — the actions
 * matter more than the ornament.
 */
function useRoomForStar() {
  const { hasDecided } = useCookieConsent();
  const [height, setHeight] = useState(() =>
    typeof window === "undefined" ? 800 : window.innerHeight
  );

  useEffect(() => {
    const measure = () => setHeight(window.innerHeight);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // The banner occupies roughly 160px of the lower screen while it's undecided.
  return height >= (hasDecided ? 560 : 700);
}

/** The words for each way a visitor can arrive. */
function Copy({ status }) {
  if (status === "unactivated") {
    return (
      <>
        <h1 style={titleStyle}>This sign is waiting to be set up.</h1>
        <p style={bodyStyle}>
          Glimt is an engraved sign with a memory book behind it. Anyone who visits can scan it and
          leave a photo or a note.
        </p>
      </>
    );
  }

  if (status === "void") {
    return (
      <>
        <h1 style={titleStyle}>This code is no longer active.</h1>
        <p style={bodyStyle}>
          It can't be used to set up a sign. If you just bought it, get in touch and we'll sort it
          out.
        </p>
      </>
    );
  }

  if (status === "orphan") {
    return (
      <>
        <h1 style={titleStyle}>This sign is already set up.</h1>
        <p style={bodyStyle}>
          We can't find the page it belongs to. Get in touch and we'll reconnect it.
        </p>
      </>
    );
  }

  if (status === "unknown") {
    return (
      <>
        <h1 style={titleStyle}>We couldn't find that sign.</h1>
        <p style={bodyStyle}>Check the code and try again — it's easy to mix up a letter.</p>
      </>
    );
  }

  if (status === "error") {
    return (
      <>
        <h1 style={titleStyle}>We couldn't check that sign.</h1>
        <p style={bodyStyle}>Something went wrong on our end. Try again in a moment.</p>
      </>
    );
  }

  return (
    <>
      <h1 style={titleStyle}>Keep their memory somewhere it can be found.</h1>
      <p style={bodyStyle}>
        An engraved sign for a grave or a garden, with a memory book behind it that family and
        friends can add to.
      </p>
    </>
  );
}
